// src/features/auth/authSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


export const verifySession = createAsyncThunk(
  'auth/verifySession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/v1/verifySession`, {
        withCredentials: true,
      });

      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }

      const user = response.data.user;
      const role = user.userType === 'school' ? 'owner' : 'user';

      return {
        role,
        data: user,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Session verification failed'
      );
    }
  }
);


// 📌 Thunk for logging in
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, role }, { rejectWithValue }) => {
    try {
      const endpoint =
        role === 'owner'
          ? `${BACKEND_URL}/api/v1/schoolLogin`
          : `${BACKEND_URL}/api/v1/usersLogin`;

      const response = await axios.post(
        endpoint,
        { email, password },
        { withCredentials: true }
      );

      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }

      return {
        role,
        data: role === 'owner' ? response.data.school : response.data.user,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Login failed'
      );
    }
  }
);

// 📌 Slice
const authSlice = createSlice({
  name: 'auth',
  initialState: {
    isAuthenticated: false,
    role: null,            // 'owner' | 'user'
    user: null,            // school or user object
    loading: false,
    error: null,
  },
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.role = null;
      state.user = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.role = action.payload.role;
        state.user = action.payload.data;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Login failed';
        state.isAuthenticated = false;
      })
      .addCase(verifySession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifySession.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.role = action.payload.role;
        state.user = action.payload.data;
      })
      .addCase(verifySession.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.role = null;
        state.error = action.payload || 'Session verification failed';
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
